import { FormatAlignCenter, FormatAlignLeft, FormatAlignRight } from '@mui/icons-material';
import {
  Box,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  Slider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { MuiColorInput } from 'mui-color-input';
import { useCallback, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from '~/components/Tooltip';
import { useStudio, type LetterCase, type StudioItem } from '../../context';
import {
  StudioSideBySide,
  StudioSideBySideContainer,
  StudioSidebarInterior,
  StudioSidebarTitle,
  transformText,
} from '../lib';

export interface StudioSettingsFormatTabTextProps {
  selectedItem: Extract<StudioItem, { type: 'text' }>;
}

export const StudioSettingsFormatTabText = ({ selectedItem }: StudioSettingsFormatTabTextProps) => {
  const disabled = selectedItem.locked;
  const { t } = useTranslation();
  const { fonts, updateItems } = useStudio();

  const handleAlignment = useCallback(
    (_: MouseEvent, align: 'center' | 'left' | 'right') => {
      updateItems([
        {
          align,
          id: selectedItem.id,
        },
      ]);
    },
    [selectedItem.id, updateItems],
  );

  const handleTransform = useCallback(
    (_: MouseEvent, transform: LetterCase) => {
      updateItems([
        {
          id: selectedItem.id,
          __textTransform: transform,
          text: transformText(selectedItem.text || '', transform),
        },
      ]);
    },
    [selectedItem.id, selectedItem.text, updateItems],
  );

  if (!fonts) return null;

  const customFonts = fonts.filter((x) => x.__fontId != null);
  const systemFonts = fonts.filter((x) => x.__fontId == null);

  return (
    <StudioSidebarInterior>
      <StudioSidebarTitle>{t('pages.studio.edit.settings.text.alignment')}</StudioSidebarTitle>

      <ToggleButtonGroup
        aria-label={t('pages.studio.edit.settings.text.alignment')}
        disabled={disabled}
        exclusive
        fullWidth
        onChange={handleAlignment}
        value={selectedItem.align}
      >
        <Tooltip title={t('pages.studio.edit.settings.text.align_left_tooltip')}>
          <ToggleButton
            aria-label={t('pages.studio.edit.settings.text.align_left_tooltip')}
            value="left"
          >
            <FormatAlignLeft />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('pages.studio.edit.settings.text.align_center_tooltip')}>
          <ToggleButton
            aria-label={t('pages.studio.edit.settings.text.align_center_tooltip')}
            value="center"
          >
            <FormatAlignCenter />
          </ToggleButton>
        </Tooltip>
        <Tooltip title={t('pages.studio.edit.settings.text.align_right_tooltip')}>
          <ToggleButton
            aria-label={t('pages.studio.edit.settings.text.align_right_tooltip')}
            value="right"
          >
            <FormatAlignRight />
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      <StudioSidebarTitle>{t('pages.studio.edit.settings.text.format')}</StudioSidebarTitle>

      <StudioSidebarInterior>
        <MuiColorInput
          disabled={disabled}
          format="hex"
          isAlphaHidden={true}
          onChange={(fill) => updateItems([{ id: selectedItem.id, fill }])}
          value={
            // eslint-disable-next-line no-restricted-syntax -- fallback for a user-picked color, not a theme color
            typeof selectedItem.fill === 'string' ? selectedItem.fill : '#000000'
          }
        />

        <Box>
          <Select
            disabled={disabled}
            onChange={(event) => {
              const font = fonts.find((x) => x.value === event.target.value);
              if (!font) return;
              updateItems([
                { __fontId: font.__fontId, id: selectedItem.id, fontFamily: font.value },
              ]);
            }}
            value={selectedItem.fontFamily}
            fullWidth
            sx={{ '& .MuiSelect-select': { paddingY: '16.5px' } }}
          >
            {customFonts.length > 0 && (
              <ListSubheader>{t('pages.studio.edit.settings.text.font_custom')}</ListSubheader>
            )}
            {customFonts.map(({ key, value }) => (
              <MenuItem key={key} value={value} sx={{ fontFamily: value, fontSize: 20, py: 1 }}>
                {key}
              </MenuItem>
            ))}
            {systemFonts.length > 0 && (
              <ListSubheader>{t('pages.studio.edit.settings.text.font_system')}</ListSubheader>
            )}
            {systemFonts.map(({ key, value }) => (
              <MenuItem key={key} value={value} sx={{ fontFamily: value, fontSize: 20, py: 1 }}>
                {key}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <StudioSideBySideContainer>
          <StudioSideBySide>
            <TextField
              disabled={disabled}
              InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
              onChange={(event) =>
                updateItems([{ id: selectedItem.id, fontSize: Number(event.target.value) }])
              }
              type="number"
              value={selectedItem.fontSize || 6}
            />
          </StudioSideBySide>
          <StudioSideBySide>
            <Slider
              disabled={disabled}
              min={6}
              max={200}
              onChange={(_, fontSize) => updateItems([{ id: selectedItem.id, fontSize }])}
              step={1}
              value={selectedItem.fontSize || 6}
              valueLabelDisplay="auto"
            />
          </StudioSideBySide>
        </StudioSideBySideContainer>

        <ToggleButtonGroup
          aria-label={t('pages.studio.edit.settings.text.transform')}
          disabled={disabled}
          exclusive
          fullWidth
          onChange={handleTransform}
          value={selectedItem.__textTransform}
        >
          <Tooltip title={t('pages.studio.edit.settings.text.uppercase_tooltip')}>
            <ToggleButton
              aria-label={t('pages.studio.edit.settings.text.uppercase_tooltip')}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
              value="uppercase"
            >
              TT
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t('pages.studio.edit.settings.text.capitalize_tooltip')}>
            <ToggleButton
              aria-label={t('pages.studio.edit.settings.text.capitalize_tooltip')}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
              value="capitalize"
            >
              Tt
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t('pages.studio.edit.settings.text.lowercase_tooltip')}>
            <ToggleButton
              aria-label={t('pages.studio.edit.settings.text.lowercase_tooltip')}
              sx={{ fontWeight: 'bold', textTransform: 'none' }}
              value="lowercase"
            >
              tt
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>

        <StudioSideBySideContainer>
          <StudioSideBySide>
            <StudioSidebarTitle>
              {t('pages.studio.edit.settings.text.line_height')}
            </StudioSidebarTitle>
          </StudioSideBySide>
          <StudioSideBySide>
            <Slider
              disabled={disabled}
              min={1}
              max={5}
              onChange={(_, lineHeight) => updateItems([{ id: selectedItem.id, lineHeight }])}
              step={0.1}
              value={selectedItem.lineHeight ?? 1}
              valueLabelDisplay="auto"
            />
          </StudioSideBySide>
        </StudioSideBySideContainer>

        <StudioSideBySideContainer>
          <StudioSideBySide>
            <StudioSidebarTitle>
              {t('pages.studio.edit.settings.text.letter_spacing')}
            </StudioSidebarTitle>
          </StudioSideBySide>
          <StudioSideBySide>
            <Slider
              disabled={disabled}
              min={0}
              max={100}
              onChange={(_, letterSpacing) => updateItems([{ id: selectedItem.id, letterSpacing }])}
              step={1}
              value={selectedItem.letterSpacing ?? 0}
              valueLabelDisplay="auto"
            />
          </StudioSideBySide>
        </StudioSideBySideContainer>
      </StudioSidebarInterior>
    </StudioSidebarInterior>
  );
};
